import React, { useState, useEffect } from 'react';
import styles from '../styles/addSingleProductDrawer.module.css';
import CustomSelect from '../customComponents/CustomSelect';
import { API_URL } from '../config/api';

const AddSingleProductDrawer = ({ onClose, onProductAdded, productToEdit }) => {
  const [UnitsOfMeasurement, setUnitsOfMeasurement] = useState('');
  const [VAT, setVAT] = useState('');
  const [Supplier, setSupplier] = useState('');
  const [Warehouse, setWarehouse] = useState('');
  const [warehouses, setWarehouses] = useState([]);

  const [barcode, setBarcode] = useState('');
  const [matchMessage, setMatchMessage] = useState('');
  const [categories, setCategories] = useState([]);
  const [units, setUnits] = useState([]);
  const [image, setImage] = useState(null);

  const [name, setName] = useState('');
  const [article, setArticle] = useState('');
  const [purchasePrice, setPurchasePrice] = useState('');
  const [sellPrice, setSellPrice] = useState('');
  const [markup, setMarkup] = useState('');
  const [notifyThreshold, setNotifyThreshold] = useState('');
  const [isMarked, setIsMarked] = useState(false);
  const [isFast, setIsFast] = useState(false);

  // ✅ одна переменная под категорию
  const [categoryId, setCategoryId] = useState(null);

  // ✅ тип товара
  const [productType, setProductType] = useState("simple");

  const token = localStorage.getItem('token');

  // ✅ Сканер в поле barcode
  useEffect(() => {
    let buffer = "";

    const handleKeyDown = (e) => {
      const activeElement = document.activeElement;
      if (!activeElement || activeElement.name !== "barcode") return;

      if (e.key === "Enter") {
        const code = buffer.trim();
        if (code) setBarcode(code);
        buffer = "";
      } else if (/^[0-9]+$/.test(e.key)) {
        buffer += e.key;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // ✅ Заполняем поля при редактировании
  useEffect(() => {
    if (!productToEdit) return;

    console.log("🧩 Данные для редактирования:", productToEdit);

    setCategoryId(productToEdit.category_id ?? null);
    setProductType(productToEdit.type || "simple");

    setUnitsOfMeasurement(productToEdit.measure_unit_id || '');
    setVAT(productToEdit.vat_type || productToEdit.vat || ''); // на всякий, если поле разное
    setSupplier(productToEdit.supplier_id || '');
    setWarehouse(productToEdit.storage_id || '');
    setBarcode(productToEdit.ntin || '');
    setName(productToEdit.name_ru || '');
    setArticle(productToEdit.article || '');
    setPurchasePrice(productToEdit.purchase_price ?? '');
    setSellPrice(productToEdit.sell_price ?? '');
    setMarkup(productToEdit.markup ?? '');
    setNotifyThreshold(productToEdit.notify_threshold ?? '');
    setIsMarked(!!productToEdit.is_marked);
    setIsFast(!!productToEdit.is_fast);
  }, [productToEdit]);

  // ✅ Автоматический пересчёт цен / наценки
  useEffect(() => {
    const handler = setTimeout(() => {
      const p = parseFloat(purchasePrice);
      const s = parseFloat(sellPrice);
      const m = parseFloat(markup);

      const activeName = document.activeElement?.name;

      if (!isNaN(p) && !isNaN(s) && activeName !== 'markup') {
        const calc = ((s - p) / p) * 100;
        if (!isNaN(calc)) setMarkup(calc.toFixed(2));
      } else if (!isNaN(p) && !isNaN(m) && activeName !== 'sellPrice') {
        const calc = p * (1 + m / 100);
        if (!isNaN(calc)) setSellPrice(calc.toFixed(2));
      } else if (!isNaN(s) && !isNaN(m) && activeName !== 'purchasePrice') {
        const calc = s / (1 + m / 100);
        if (!isNaN(calc)) setPurchasePrice(calc.toFixed(2));
      }
    }, 700);

    return () => clearTimeout(handler);
  }, [purchasePrice, sellPrice, markup]);

  useEffect(() => {
    const checkBarcode = async () => {
      if (!barcode) {
        setMatchMessage('');
        return;
      }

      try {
        const res = await fetch(`${API_URL}/api/ncp?ntin=${barcode}`);
        const result = await res.json();

        if (result?.data && Array.isArray(result.data) && result.data.length > 0) {
          // ✅ нашли — подставляем имя из базы
          setName(result.data[0].name_ru || '');
          setMatchMessage('✅ Совпадение найдено');
        } else {
          // ✅ не нашли — просто показываем сообщение, НО name не трогаем
          setMatchMessage('❌ Совпадение не найдено');
        }
      } catch (err) {
        console.error('Ошибка при проверке штрихкода:', err);
        setMatchMessage('Ошибка при проверке');
      }
    };

    checkBarcode();
  }, [barcode]);


  // ✅ Категории
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await fetch(`${API_URL}/api/product_categories`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });

        const result = await res.json();

        if (Array.isArray(result.data)) {
          const formatted = result.data.map(cat => ({
            value: cat.id,
            label: cat.title,
          }));
          setCategories(formatted);
          localStorage.setItem('categories', JSON.stringify(formatted));
        } else {
          console.warn('⚠️ Ожидался массив категорий, но пришло:', result);
        }
      } catch (err) {
        console.error('Ошибка при получении категорий:', err);
        const saved = localStorage.getItem('categories');
        if (saved) setCategories(JSON.parse(saved));
      }
    };

    if (token) fetchCategories();
    else console.error('❌ Нет токена! Авторизация не выполнена.');
  }, [token]);

  // ✅ Единицы измерения
  useEffect(() => {
    const fetchUnits = async () => {
      try {
        const res = await fetch(`${API_URL}/api/measure_units`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        const result = await res.json();
        if (Array.isArray(result.data)) {
          const formatted = result.data.map(unit => ({
            value: unit.id,
            label: `${unit.unit_title} (${unit.unit_value})`,
          }));
          setUnits(formatted);
          localStorage.setItem('units', JSON.stringify(formatted));
        } else {
          console.warn('⚠️ Ожидался массив единиц измерения, но пришло:', result);
        }
      } catch (err) {
        console.error('Ошибка при получении единиц измерения:', err);
        const saved = localStorage.getItem('units');
        if (saved) setUnits(JSON.parse(saved));
      }
    };

    if (token) fetchUnits();
  }, [token]);

  // ✅ Склады
  useEffect(() => {
    const fetchWarehouses = async () => {
      try {
        const res = await fetch(`${API_URL}/api/storages`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        const result = await res.json();
        if (Array.isArray(result.data)) {
          const formatted = result.data.map(storage => ({
            value: storage.id,
            label: storage.name,
          }));
          setWarehouses(formatted);
          localStorage.setItem('warehouses', JSON.stringify(formatted));
        } else {
          console.warn('⚠️ Ожидался массив складов, но пришло:', result);
        }
      } catch (err) {
        console.error('Ошибка при загрузке складов:', err);
        const saved = localStorage.getItem('warehouses');
        if (saved) setWarehouses(JSON.parse(saved));
      }
    };

    if (token) fetchWarehouses();
  }, [token]);

  const generateBarcode = () => {
    const randomBarcode = Math.floor(100000000000000 + Math.random() * 900000000000000);
    setBarcode(String(randomBarcode));
  };

  const handleImageUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validTypes = ['image/png', 'image/jpeg'];
    const maxSize = 1024 * 1024;

    if (!validTypes.includes(file.type)) {
      alert('Неверный формат. Допустимы только PNG или JPG.');
      return;
    }

    if (file.size > maxSize) {
      alert('Файл слишком большой. Максимум 1MB.');
      return;
    }

    const img = new Image();
    img.src = URL.createObjectURL(file);

    img.onload = () => {
      const { width, height } = img;
      if (width !== 1080 || height !== 1080) {
        alert('Неверное разрешение. Требуется 1080x1080 пикселей.');
        return;
      }
      if (width / height !== 1) {
        alert('Соотношение сторон должно быть 1:1.');
        return;
      }
      setImage(img.src);
    };
  };

  const handleSave = async () => {
    if (!token) {
      alert('Нет токена. Авторизуйтесь заново.');
      return;
    }

    const payload = {
      name_ru: name?.trim() || '',
      article: article?.trim() || '',
      ntin: barcode?.trim() || '',
      purchase_price: parseFloat(purchasePrice) || 0,
      sell_price: parseFloat(sellPrice) || 0,
      markup: parseFloat(markup) || 0,

      // ✅ только один ключ
      category_id: categoryId,

      type: productType,
      measure_unit_id: UnitsOfMeasurement || null,
      supplier_id: Supplier || null,
      storage_id: Warehouse || null,
      notify_threshold: parseInt(notifyThreshold, 10) || 0,
      is_fast: !!isFast,
      is_marked: !!isMarked,

      // если у тебя бэку нужно VAT:
      vat_type: VAT || null,
    };

    console.log("📦 Отправляем payload:", payload);

    try {
      let url = `${API_URL}/api/products`;
      let method = "POST";

      if (productToEdit?.id) {
        url = `${API_URL}/api/products/${productToEdit.id}`;
        method = "PUT";
      }

      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      console.log('Ответ API:', data);

      if (!res.ok) {
        alert(`Ошибка (${res.status}): ${data?.errors?.message || 'Неизвестная ошибка'}`);
        return;
      }

      onProductAdded?.();
      onClose?.();
    } catch (err) {
      console.error("Ошибка при сохранении:", err);
      alert(`Ошибка при сохранении товара: ${err.message}`);
    }
  };

  return (
    <div className={styles.drawer}>
      <div className={styles.header}>
        <button onClick={onClose} className={styles.backButton}></button>
        <h2 className={styles.headerText}>Добавление однотипного товара</h2>
      </div>

      <div className={styles.content}>
        <div className={styles.section}>
          <h3>Общая информация</h3>
          <div className={styles.grid}>
            <input
              className={styles.input}
              placeholder="Наименование"
              value={name}
              onChange={e => setName(e.target.value)}
            />

            <CustomSelect
              options={categories}
              value={categoryId}
              onChange={setCategoryId}
              placeholder="Категория"
            />

            <CustomSelect
              className={styles.filters__select}
              options={units}
              value={UnitsOfMeasurement}
              onChange={setUnitsOfMeasurement}
              placeholder="Единица измерения"
            />

            <input
              className={styles.input}
              placeholder="Артикул"
              value={article}
              onChange={e => setArticle(e.target.value)}
            />
          </div>
        </div>

        <div className={styles.section}>
          <h3>Стоимость</h3>
          <div className={styles.grid}>
            <input
              className={styles.input}
              name="purchasePrice"
              placeholder="Цена закупки (₸)"
              value={purchasePrice}
              onChange={e => setPurchasePrice(e.target.value)}
            />
            <input
              className={styles.input}
              name="sellPrice"
              placeholder="Цена продажи (₸)"
              value={sellPrice}
              onChange={e => setSellPrice(e.target.value)}
            />
            <div className={styles.procent}>
              <input
                className={styles.input}
                name="markup"
                placeholder="Накрутка (%)"
                value={markup}
                onChange={e => setMarkup(e.target.value)}
              />
            </div>

            <CustomSelect
              className={styles.filters__select}
              options={[
                { value: "standard", label: "С НДС" },
                { value: "without_vat", label: "Без НДС" },
                { value: "vat_0", label: "НДС 0%" },
                { value: "not_taxable", label: "Не облагается НДС" },
              ]}
              value={VAT}
              onChange={setVAT}
              placeholder="ТИП НДС"
            />

            <CustomSelect
              className={styles.filters__select}
              options={[
                { value: "simple", label: "Товар однотипный" },
                { value: "variable", label: "Товар многотипный" },
                { value: "composite", label: "Составной товар" },
                { value: "service", label: "Услуга" },
                { value: "universal", label: "Универсальный товар" },
              ]}
              value={productType}
              onChange={setProductType}
              placeholder="Тип значения"
            />

            <button
              type="button"
              className={styles.clearButton}
              onClick={() => {
                setPurchasePrice('');
                setSellPrice('');
                setMarkup('');
              }}
            >
              Очистить
            </button>
          </div>
        </div>

        <div className={styles.section}>
          <h3>Поставщик</h3>
          <div className={styles.grid}>
            <CustomSelect
              className={styles.filters__select}
              options={[
                { value: "market_group", label: "ТОО Market Group" },
                { value: "tehsnab", label: 'ТОО "ТехСнаб"' },
                { value: "bahyt", label: 'ИП "Бахыт"' },
                { value: "global", label: 'ТОО "Глобал Снаб"' },
              ]}
              value={Supplier}
              onChange={setSupplier}
              placeholder="Поставщик"
            />

            <CustomSelect
              className={styles.filters__select}
              options={warehouses}
              value={Warehouse}
              onChange={setWarehouse}
              placeholder="Склад"
            />

            <input className={styles.input} placeholder="Количество" type="number" />

            <input
              className={styles.input}
              placeholder="Уведомить при остатке"
              type="number"
              value={notifyThreshold}
              onChange={e => setNotifyThreshold(e.target.value)}
            />
          </div>
        </div>

        <div className={styles.section}>
          <h3>Штрихкод</h3>
          <div className={styles.barcodeRow}>
            <div className={styles.barcodeRowBlock}>
              <input
                className={styles.input}
                name="barcode"
                placeholder="Штрихкод"
                value={barcode}
                onChange={(e) => setBarcode(e.target.value)}
              />
              {matchMessage && (
                <p
                  style={{
                    position: 'absolute',
                    marginTop: '5px',
                    fontSize: '14px',
                    color: matchMessage.includes('не найдено') ? 'red' : 'green'
                  }}
                >
                  {matchMessage}
                </p>
              )}
            </div>
            <button type="button" className={styles.link}>+ Добавить доп. штрихкод</button>
          </div>
          <button type="button" className={styles.barcodeRowRandom} onClick={generateBarcode}>
            Сгенерировать
          </button>
        </div>

        <div className={styles.section}>
          <h3>Фото</h3>
          <div
            className={styles.uploadBox}
            onClick={() => document.getElementById('photoInput')?.click()}
          >
            {image ? (
              <img src={image} alt="preview" className={styles.previewImage} />
            ) : (
              <div className={styles.uploadPlaceholder}>
                <p className={styles.uploadPlaceholderPlus}>+</p>
                <p className={styles.uploadPlaceholderText}>Загрузить фото</p>
              </div>
            )}
            <input
              type="file"
              id="photoInput"
              accept="image/png, image/jpeg"
              onChange={handleImageUpload}
              style={{ display: 'none' }}
            />
            <p className={styles.uploadInfo}>
              Тип файла: <strong>PNG, JPG</strong><br />
              Соотношение сторон: <strong>1:1 (квадрат)</strong><br />
              Разрешение: <strong>1080×1080 px</strong><br />
              Максимальный размер: <strong>1Mb</strong>
            </p>
          </div>
        </div>

        <div className={styles.checkboxRow}>
          <label className={styles.checkboxContainer}>
            <input
              className={styles.input}
              type="checkbox"
              checked={isMarked}
              onChange={e => setIsMarked(e.target.checked)}
            />
            <span className={styles.customCheckbox}></span>
            <p>Маркированный товар</p>
          </label>

          <label className={styles.checkboxContainer}>
            <input
              className={styles.input}
              type="checkbox"
              checked={isFast}
              onChange={e => setIsFast(e.target.checked)}
            />
            <span className={styles.customCheckbox}></span>
            <p>Быстрый товар</p>
          </label>
        </div>

        <div className={styles.footer}>
          <button className={styles.addButton} onClick={handleSave}>
            {productToEdit ? "Сохранить изменения" : "Добавить товар"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddSingleProductDrawer;
